import { type FC, useContext } from "react";
import { useIntl } from "react-intl";
import { MenuItem, Stack } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import { ContextMenu } from "@packsize/components";

import TwoLineDropdownItem from "@/components/dropdown/two-line-dropdown-item";
import { OrdersContext } from "@/views/production/orders/orders.context";
import { useCanManageJobs } from "@/hooks/use-can-manage-jobs";
import { type Batch } from "@/schemas/routing/batch";

interface AvailableBatchessActionMenuProps {
	readonly batch: Batch;
}

export const AvailableBatchesActionMenu: FC<AvailableBatchessActionMenuProps> = ({ batch }) => {
	const intl = useIntl();
	const { batchId, alias } = batch;
	const canManageJobs = useCanManageJobs();
	const { scanToTriggerSingleConfigured, removeJob, produceNext, produceNow, triggerSingleJob } =
		useContext(OrdersContext);

	return (
		<ContextMenu
			popoverProps={{
				anchorOrigin: {
					vertical: "bottom",
					horizontal: "center",
				},
				transformOrigin: {
					vertical: "top",
					horizontal: "right",
				},
			}}
		>
			{canManageJobs === true && (
				<MenuItem onClick={() => removeJob(batchId)}>
					<Stack direction="row" alignItems="center" spacing={2}>
						<HighlightOffIcon sx={{ fontSize: 24 }} />
						<TwoLineDropdownItem
							primaryText={intl.formatMessage({ id: "Cancel Production", defaultMessage: "Cancel Production" })}
							secondaryText={intl.formatMessage({ id: "Do not produce", defaultMessage: "Do not produce" })}
						/>
					</Stack>
				</MenuItem>
			)}
			{scanToTriggerSingleConfigured === true && (
				<MenuItem onClick={() => triggerSingleJob(batchId)} disabled={Boolean(batch.blockedReason)}>
					<Stack direction="row" alignItems="center" spacing={2}>
						<MoveUpIcon sx={{ fontSize: 24 }} />
						<TwoLineDropdownItem
							primaryText={intl.formatMessage({ id: "Trigger", defaultMessage: "Trigger" })}
							secondaryText={intl.formatMessage({
								id: "Produce one job in batch",
								defaultMessage: "Produce one job in batch",
							})}
						/>
					</Stack>
				</MenuItem>
			)}
			{scanToTriggerSingleConfigured === false && (
				<MenuItem onClick={() => produceNext({ batchId, alias })} disabled={Boolean(batch.blockedReason)}>
					<Stack direction="row" alignItems="center" spacing={2}>
						<MoveUpIcon sx={{ fontSize: 24 }} />
						<TwoLineDropdownItem
							primaryText={intl.formatMessage({ id: "Produce Next", defaultMessage: "Produce Next" })}
							secondaryText={intl.formatMessage({
								id: "Produce after current job",
								defaultMessage: "Produce after current job",
							})}
						/>
					</Stack>
				</MenuItem>
			)}
			<MenuItem onClick={() => produceNow({ batchId, alias })} disabled={Boolean(batch.blockedReason)}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<UpgradeIcon sx={{ fontSize: 24 }} />
					<TwoLineDropdownItem
						primaryText={intl.formatMessage({ id: "Produce Now", defaultMessage: "Produce Now" })}
						secondaryText={intl.formatMessage({
							id: "Pause current job to produce",
							defaultMessage: "Pause current job to produce",
						})}
					/>
				</Stack>
			</MenuItem>
		</ContextMenu>
	);
};
